import React, { useMemo } from 'react';
import styled from "styled-components";
import { HeadlineMedium, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { useSelector } from "react-redux";
import { selectHolidayUserInfo, selectUsersHolidayInfo } from '@features/holidays/store/holidays-v2.duck';
import { selectConfig } from '@shared/store/config.duck';
import { useTranslation } from "react-i18next";
import { selectCurrentUser } from '@shared/store/auth.duck';
import { Permission } from '@shared/models/user.models';
import { WithUsersLineReports } from '@shared/hooks/WithUsersLineReports';
import { CustomLeaveInfo } from "./CustomLeaveInfo";
import { LeaveBreakdownList } from "./LeaveBreakdownList";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function HolidaysTakenOverview(props: Props) {
  const usersHolidayInfo = useSelector(selectUsersHolidayInfo);
  const userInfo = useSelector(selectHolidayUserInfo);
  const config = useSelector(selectConfig);
  const currentUser = useSelector(selectCurrentUser);
  const {t} = useTranslation();

  const {lineReports} = WithUsersLineReports(currentUser);

  const isHolidayTrackingEnabled = currentUser?.companyEntity.enableHolidayBooking;

  const addHolidayUrl = useMemo(() => {
    if (lineReports.length > 0) {
      return '/holidays/add'
    }
    let qs = '';
    if (currentUser?.role === Permission.Basic || currentUser?.role === Permission.TeamAdmin) {
      qs += `?uid=${currentUser.id}`
    }
    return '/holidays/add' + qs

  }, [currentUser, lineReports]);

  return (
    <Container data-test={"holidays-taken-overview"}>
      <HeadlineSmall style={{marginBottom: 16}}>{(!userInfo.userId || userInfo.userId === currentUser?.id) ? t('holiday.your-summary') : `${userInfo.firstName} ${userInfo.lastName}`}</HeadlineSmall>
      <BodyRegular style={{marginBottom: -4}} colour={Colours.darkGrey} weight={600}>{t('holiday.days-taken-or-booked')}</BodyRegular>
      <HeadlineMedium style={{marginBottom: 16}} color={Colours.blue}>{usersHolidayInfo.daysTaken}</HeadlineMedium>

      {isHolidayTrackingEnabled && <>
        {config.holidayAllowance &&
          <>
            <HorizontalSpacer style={{marginBottom: 16}} />
            <BodyRegular style={{marginBottom: -4}} colour={Colours.darkGrey} weight={600}>{t('holiday.days-left')}</BodyRegular>
            <HeadlineMedium color={Colours.blue}>{usersHolidayInfo.daysLeft}</HeadlineMedium>
          </>
        }

        {isHolidayTrackingEnabled && <PrimaryButton link={addHolidayUrl}
                                                    dataTest={"add-holidays-button"}
                                                    text={isHolidayTrackingEnabled ? 'button.add-holiday-request' : 'button.add-holiday'}
                                                    fullWidth={true}
                                                    style={{marginBottom: 8, marginTop: 12}}
                                                    size={ButtonSize.Small} />}

        {config.autoCarryOverEnabled && usersHolidayInfo && <LeaveBreakdownList usersHolidayInfo={usersHolidayInfo} />}

        {usersHolidayInfo.customLeaveDays.length > 0 && <CustomLeaveInfo customLeave={usersHolidayInfo.customLeaveDays} />}
      </>}
    </Container>
  )
}

interface Props {
}

const Container = styled.div`
  width: 100%;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 24px;
  padding: 24px 32px;
  
`
